import { formatDate } from './format'
import {
  reportDate,
  reportDaySpan,
  reportInstant,
  reportWeekday,
  shiftReportDate,
} from './report-zone'

/**
 * The period presets the dashboard and the report surface share
 * (CHG-2026-09-manager-reporting).
 *
 * `today` is not a fourth length — it is a different question. A running day compared against a
 * whole previous day puts the owner in the red every lunchtime, so it carries its own comparison:
 * the same weekday one week earlier, truncated to the same time of day. That truncation is not a
 * special case in the backend either; the window simply ends at "now" and is shifted by seven days.
 */
export type PeriodPreset = 'today' | '7d' | '14d' | '30d' | 'custom'

export type Comparison = 'previous_period' | 'same_weekday_last_week'

/**
 * What a click in the picker's grid selects (CHG-2026-09-smarter-period-picker).
 *
 * Carried with the range rather than derived from it, because it is the difference between
 * "31.08. – 06.09." and "KW 36": the same seven days, but only one of them is what the reader
 * asked for, and only that one can be stepped a week at a time later.
 */
export type PeriodGrain = 'day' | 'week' | 'month' | 'quarter'

/** A range the reader picked, with the times only present when they switched them on. */
export interface CustomRange {
  /** `YYYY-MM-DD`, Vienna calendar days. */
  from: string
  to: string
  /** `HH:mm` wall-clock, Vienna. Absent means whole days — the overwhelmingly common case. */
  fromTime?: string
  toTime?: string
  grain?: PeriodGrain
}

/** Whole days, i.e. the times are absent or cover the day end to end. */
export function isWholeDayRange(
  range: CustomRange | null | undefined,
): boolean {
  if (!range) return true
  const from = range.fromTime ?? '00:00'
  const to = range.toTime ?? '24:00'
  return from === '00:00' && (to === '24:00' || to === '00:00')
}

export interface ResolvedPeriod {
  /** `YYYY-MM-DD` for a whole-day window, a full instant for the running day. */
  from: string
  to: string
  comparison: Comparison
  /** True while the window's last day is still running, so figures are partial. */
  running: boolean
  /** Vienna calendar days covered, for labelling and for the trend's bucket count. */
  days: number
}

const PRESET_DAYS: Record<Exclude<PeriodPreset, 'today' | 'custom'>, number> = {
  '7d': 7,
  '14d': 14,
  '30d': 30,
}

/** The instant, with seconds and milliseconds cut off — a value that is equal across renders. */
function startOfMinute(now: Date): string {
  return new Date(Math.floor(now.getTime() / 60_000) * 60_000).toISOString()
}

export function resolvePeriod(
  preset: PeriodPreset,
  now: Date,
  custom?: CustomRange,
): ResolvedPeriod {
  if (preset === 'today') {
    const today = reportDate(now)
    return {
      // A bare date on `from` lets the backend anchor it to Vienna midnight; `to` is the actual
      // instant, which is what makes the comparison window truncate to the same time of day.
      //
      // Truncated to the minute, and that is not cosmetic: this string goes into the query string
      // and the query string IS the TanStack Query key. At millisecond precision every render
      // produced a key nobody had ever fetched, so the query never settled — the dashboard sat on
      // its loading skeletons for ever and refetched in a loop, tens of thousands of requests deep.
      // A running day is not more honest for being accurate to the millisecond.
      from: today,
      to: startOfMinute(now),
      comparison: 'same_weekday_last_week',
      running: true,
      days: 1,
    }
  }

  if (preset === 'custom') {
    const fromDay = custom?.from ?? reportDate(now)
    const toDay = custom?.to ?? reportDate(now)
    const days = reportDaySpan(fromDay, toDay)
    // Whole days stay bare dates: the endpoint anchors those to Vienna midnight itself, and a
    // date is what every existing link, drill-through and test already carries.
    if (isWholeDayRange(custom)) {
      const end = windowEnd(reportInstant(toDay, '24:00'), toDay, now)
      return {
        from: fromDay,
        to: end.to,
        comparison: 'previous_period',
        running: end.running,
        days,
      }
    }
    const nominalEnd = reportInstant(toDay, custom?.toTime ?? '24:00')
    const end = windowEnd(nominalEnd, nominalEnd, now)
    return {
      from: reportInstant(fromDay, custom?.fromTime ?? '00:00'),
      to: end.to,
      comparison: 'previous_period',
      running: end.running,
      days,
    }
  }

  const days = PRESET_DAYS[preset]
  const lastDay = reportDate(now)
  const end = windowEnd(reportInstant(lastDay, '24:00'), lastDay, now)
  return {
    from: shiftReportDate(lastDay, -(days - 1)),
    to: end.to,
    comparison: 'previous_period',
    running: end.running,
    days,
  }
}

/**
 * Where a window actually stops, and whether it is still filling.
 *
 * A window that reaches into the future is cut at "now", and that is the whole comparison fix: the
 * backend derives the previous window by shifting BOTH ends back by the same number of Vienna
 * days, so an end of "now" truncates the previous window at the same time of day for free. Sent as
 * the nominal end instead — the end of today, because a bare `YYYY-MM-DD` means the whole day — the
 * last seven days at 11:00 were measured against seven COMPLETE days, and the delta read as a loss
 * every morning. The running-day preset always did it this way; every other period did not.
 *
 * A window whose end has already passed keeps its finished form, so a whole-day range stays the
 * bare dates that every existing link, drill-through and test carries.
 */
function windowEnd(
  nominalEnd: string,
  finishedForm: string,
  now: Date,
): { to: string; running: boolean } {
  if (Date.parse(nominalEnd) <= now.getTime()) {
    return { to: finishedForm, running: false }
  }
  return { to: startOfMinute(now), running: true }
}

/** The window the comparison figures come from, for naming it in the UI. */
export function comparisonWindow(
  period: ResolvedPeriod,
  now: Date,
): { from: string; to: string } {
  const shift = period.comparison === 'same_weekday_last_week' ? 7 : period.days
  const fromDay = period.from.length === 10 ? period.from : reportDate(now)
  const toDay = period.to.length === 10 ? period.to : reportDate(now)
  return {
    from: shiftReportDate(fromDay, -shift),
    to: shiftReportDate(toDay, -shift),
  }
}

/** Every day in the window, so a trend can render a bar for a day that produced no row. */
export function periodDays(period: ResolvedPeriod, now: Date): Array<string> {
  const last = period.to.length === 10 ? period.to : reportDate(now)
  const out: Array<string> = []
  for (let i = period.days - 1; i >= 0; i -= 1)
    out.push(shiftReportDate(last, -i))
  return out
}

/**
 * How a two-click date range is built.
 *
 * Deliberately not react-day-picker's own range handling: that reports the first click as an
 * already-complete range (`from === to`) and then treats the next click as the start of a new
 * one, which made a two-click range impossible — every pick collapsed to a single day.
 *
 * First click sets the start. The second extends it, in either direction, so picking the later
 * day first still yields a range. A click on a finished range starts over.
 */
export function extendDayRange(
  previous: { from?: Date; to?: Date } | undefined,
  day: Date,
): { from: Date; to: Date | undefined } {
  if (!previous?.from || previous.to) return { from: day, to: undefined }
  return day < previous.from
    ? { from: day, to: previous.from }
    : { from: previous.from, to: day }
}

// ---------------------------------------------------------------------------------------------
// Natural periods
//
// "Letzte Woche" is what somebody asks for; "7 T" is how a machine says it — and it ends today,
// so it cuts last week in half. These resolve against the Vienna calendar, and every one of them
// stops at today: a month that has not finished yet must not report its remaining days as zero.
// ---------------------------------------------------------------------------------------------

export type NaturalPeriod = NaturalPeriodId

export type NaturalPeriodId =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'this-year'

export const NATURAL_PERIODS: Array<NaturalPeriodId> = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'this-month',
  'last-month',
  'this-quarter',
  'this-year',
]

/** The Monday of the Vienna week `date` falls in. */
export function startOfReportWeek(date: string): string {
  return shiftReportDate(date, -(reportWeekday(date) - 1))
}

/** `YYYY-MM-DD` of the first day of that month. */
export function startOfReportMonth(date: string): string {
  return `${date.slice(0, 7)}-01`
}

/** The last day of the month `date` falls in, offset by whole months. */
export function endOfReportMonth(date: string, monthOffset = 0): string {
  const year = Number(date.slice(0, 4))
  const month = Number(date.slice(5, 7))
  // Day 0 of the following month is the last day of this one, and Date handles the year rollover —
  // month arithmetic done on the string would need a January special case.
  const anchor = new Date(Date.UTC(year, month + monthOffset, 0, 12))
  return reportDate(anchor)
}

/** The first day of the month `monthOffset` months from the one `date` falls in. */
export function shiftReportMonth(date: string, monthOffset: number): string {
  const year = Number(date.slice(0, 4))
  const month = Number(date.slice(5, 7))
  const anchor = new Date(Date.UTC(year, month - 1 + monthOffset, 1, 12))
  return startOfReportMonth(reportDate(anchor))
}

/**
 * Which period a quick-list entry resolves to, in four characters: `07.09.`, `KW 36`, `Sep`, `Q3`.
 *
 * The label alone leaves the reader to find out after the click. "Letzte Woche" is unambiguous
 * about the intent and says nothing about the dates, and "Dieses Quartal" says nothing at all to
 * somebody who has not counted the months.
 */
export function naturalPeriodHint(
  t: Translate,
  id: NaturalPeriodId,
  now: Date,
): string {
  const range = resolveNaturalPeriod(id, now)
  switch (id) {
    case 'today':
    case 'yesterday':
      return dayAndMonth(range.from)
    case 'this-week':
    case 'last-week':
      return t('period.weekShort', { week: isoWeekOf(range.from) })
    case 'this-month':
    case 'last-month':
      return t(`period.monthsShort.${Number(range.from.slice(5, 7)) - 1}`)
    case 'this-quarter':
      return t('period.quarter', {
        quarter: Math.floor((Number(range.from.slice(5, 7)) - 1) / 3) + 1,
      })
    case 'this-year':
      return range.from.slice(0, 4)
  }
}

/** `07.09.` — no year: the quick list is all recent, and the year would cost the column. */
function dayAndMonth(date: string): string {
  return `${date.slice(8, 10)}.${date.slice(5, 7)}.`
}

export function resolveNaturalPeriod(
  id: NaturalPeriodId,
  now: Date,
): CustomRange {
  const today = reportDate(now)
  switch (id) {
    case 'today':
      return { from: today, to: today, grain: 'day' }
    case 'yesterday': {
      const day = shiftReportDate(today, -1)
      return { from: day, to: day, grain: 'day' }
    }
    case 'this-week':
      return { from: startOfReportWeek(today), to: today, grain: 'week' }
    case 'last-week': {
      const start = shiftReportDate(startOfReportWeek(today), -7)
      return { from: start, to: shiftReportDate(start, 6), grain: 'week' }
    }
    case 'this-month':
      return { from: startOfReportMonth(today), to: today, grain: 'month' }
    case 'last-month': {
      const start = shiftReportMonth(today, -1)
      return { from: start, to: endOfReportMonth(start), grain: 'month' }
    }
    case 'this-quarter': {
      const month = Number(today.slice(5, 7))
      const firstMonth = Math.floor((month - 1) / 3) * 3 + 1
      const start = `${today.slice(0, 4)}-${String(firstMonth).padStart(2, '0')}-01`
      return { from: start, to: today, grain: 'quarter' }
    }
    case 'this-year':
      return { from: `${today.slice(0, 4)}-01-01`, to: today, grain: 'quarter' }
  }
}

/**
 * A range moved by its own length — one click for "and the week before that".
 *
 * A grain-aware step, not a day count: a month range moves to the previous whole month rather than
 * back by 30 days, which for the 31st would land in the wrong month and for February in the next
 * one. Never steps past today; returns null when it would.
 */
export function stepPeriod(
  range: CustomRange,
  direction: -1 | 1,
  now: Date,
): CustomRange | null {
  const today = reportDate(now)
  const grain = range.grain ?? 'day'
  let next: CustomRange

  if (grain === 'month') {
    const start = shiftReportMonth(range.from, direction)
    next = { ...range, from: start, to: endOfReportMonth(start) }
  } else if (grain === 'quarter') {
    const start = shiftReportMonth(range.from, direction * 3)
    next = { ...range, from: start, to: endOfReportMonth(start, 2) }
  } else {
    const days = reportDaySpan(range.from, range.to)
    next = {
      ...range,
      from: shiftReportDate(range.from, direction * days),
      to: shiftReportDate(range.to, direction * days),
    }
  }

  if (next.from > today) return null
  // Stepping forward into a period that has not finished stops at today, so the last step of the
  // year does not report tomorrow as a day without revenue.
  return { ...next, to: next.to > today ? today : next.to }
}

/** The ISO week number of a Vienna calendar day. */
export function isoWeekOf(date: string): number {
  // The Thursday of that week decides which year the week belongs to — the ISO-8601 rule, and the
  // reason a naive "day of year / 7" is wrong for the first and last week of every year.
  const thursday = shiftReportDate(date, 4 - reportWeekday(date))
  const jan4 = `${thursday.slice(0, 4)}-01-04`
  const firstThursday = shiftReportDate(jan4, 4 - reportWeekday(jan4))
  const diff =
    Date.parse(`${thursday}T12:00:00Z`) -
    Date.parse(`${firstThursday}T12:00:00Z`)
  return Math.round(diff / 604_800_000) + 1
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * The Vienna day a resolved endpoint falls on, whether it is a bare `YYYY-MM-DD` or a full instant.
 *
 * `resolvePeriod` returns both shapes — a running day and a range carrying a time of day both end
 * at an instant — and pasting `T12:00:00Z` onto one of those yields `…000ZT12:00:00Z`. That is how
 * the report page died with "Invalid time value" the moment somebody picked Heute.
 */
export function reportDayOf(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : reportDate(new Date(value))
}

/**
 * The window a page has resolved, as a heading.
 *
 * One function for the three page headers that showed it, because each of them was pasting the
 * endpoints together itself and each of them broke on the same shape.
 */
export function describeResolvedRange(
  t: Translate,
  resolved: ResolvedPeriod,
): string {
  const from = reportDayOf(resolved.from)
  const to = reportDayOf(resolved.to)
  if (from === to) return formatDate(`${from}T12:00:00Z`)
  return t('period.range', {
    from: formatDate(`${from}T12:00:00Z`),
    to: formatDate(`${to}T12:00:00Z`),
  })
}

/**
 * A picked range in the reader's own words.
 *
 * A week reads "KW 36", a month "September 2026" — the same days either way, but the label says
 * which question was asked, and a bare date span throws that away. Lives here rather than in the
 * picker because the trigger, the collapsed filter bar and the page header must all say the same
 * thing about the same range.
 */
export function describeRange(t: Translate, range: CustomRange): string {
  const time = isWholeDayRange(range)
    ? ''
    : ` · ${range.fromTime ?? '00:00'}–${range.toTime ?? '24:00'}`
  const monthIndex = Number(range.from.slice(5, 7)) - 1
  const year = range.from.slice(0, 4)

  if (range.grain === 'month' && range.to === endOfReportMonth(range.from)) {
    return `${t(`period.months.${monthIndex}`)} ${year}${time}`
  }
  if (
    range.grain === 'quarter' &&
    range.to === endOfReportMonth(range.from, 2)
  ) {
    return `${t('period.quarter', { quarter: Math.floor(monthIndex / 3) + 1 })} ${year}${time}`
  }
  if (range.grain === 'week' && reportDaySpan(range.from, range.to) === 7) {
    return `${t('period.weekShort', { week: isoWeekOf(range.from) })}${time}`
  }
  if (range.from === range.to) {
    return `${formatDate(`${range.from}T12:00:00Z`)}${time}`
  }
  return `${formatDate(`${range.from}T12:00:00Z`)} – ${formatDate(`${range.to}T12:00:00Z`)}${time}`
}
